import { AttributionModel as AttributionDistribution } from "@redotech/crdb-enums/attribution/attribution-model";
import {
  AttributionEvidenceType,
  type ATTRIBUTION_TIME_UNITS,
} from "@redotech/redo-attribution-common/attribution-settings";
import { TimeUnit } from "@redotech/redo-common-model/time";
import { t } from "@redotech/redo-i18n";
import LayersIcon from "@redotech/redo-icons/layers-three-02.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageChatIcon from "@redotech/redo-icons/message-chat-circle.svg";
import type { FunctionComponent } from "react";

export function distributionOptions(): {
  value: AttributionDistribution;
  text: string;
}[] {
  return [
    { value: AttributionDistribution.LastTouch, text: t("Last touch") },
    { value: AttributionDistribution.Linear, text: t("Multi-touch (linear)") },
  ];
}

/** How settings group evidence: a channel's own, or evidence any message can carry. */
export enum EvidenceSection {
  EMAIL = "email",
  SMS = "sms",
  ALL_MESSAGES = "all_messages",
}

export const EVIDENCE_SECTION: Record<
  AttributionEvidenceType,
  EvidenceSection
> = {
  [AttributionEvidenceType.EMAIL_DELIVERED]: EvidenceSection.EMAIL,
  [AttributionEvidenceType.EMAIL_OPEN]: EvidenceSection.EMAIL,
  [AttributionEvidenceType.EMAIL_CLICK]: EvidenceSection.EMAIL,
  [AttributionEvidenceType.SMS_SEND]: EvidenceSection.SMS,
  [AttributionEvidenceType.SMS_CLICK]: EvidenceSection.SMS,
  [AttributionEvidenceType.CODE_REDEEMED]: EvidenceSection.ALL_MESSAGES,
};

export function evidenceSectionMeta(): Record<
  EvidenceSection,
  {
    name: string;
    description: string;
    Icon: FunctionComponent<{ className?: string }>;
  }
> {
  return {
    [EvidenceSection.EMAIL]: {
      name: t("Email"),
      description: t("Every email: campaign, automation and Commerce Agent"),
      Icon: MailIcon,
    },
    [EvidenceSection.SMS]: {
      name: "SMS",
      description: t("Every text: campaign, automation and Commerce Agent"),
      Icon: MessageChatIcon,
    },
    [EvidenceSection.ALL_MESSAGES]: {
      name: t("All messages"),
      description: t("Evidence that applies to emails and texts alike"),
      Icon: LayersIcon,
    },
  };
}

/** `editableWindow` is false where settings parsing fixes the window. */
export function evidenceMeta(): Record<
  AttributionEvidenceType,
  { name: string; description: string; editableWindow: boolean }
> {
  return {
    [AttributionEvidenceType.EMAIL_DELIVERED]: {
      name: t("Delivered"),
      description: t("The email was delivered to the inbox"),
      editableWindow: true,
    },
    [AttributionEvidenceType.EMAIL_OPEN]: {
      name: t("Opened"),
      description: t("The customer opened the email"),
      editableWindow: true,
    },
    [AttributionEvidenceType.EMAIL_CLICK]: {
      name: t("Clicked"),
      description: t("The customer clicked a link in the email"),
      editableWindow: true,
    },
    [AttributionEvidenceType.SMS_SEND]: {
      name: t("Sent"),
      description: t("The text was sent"),
      editableWindow: true,
    },
    [AttributionEvidenceType.SMS_CLICK]: {
      name: t("Clicked"),
      description: t("The customer clicked a link in the text"),
      editableWindow: true,
    },
    [AttributionEvidenceType.CODE_REDEEMED]: {
      name: t("Codes redeemed"),
      description: t(
        "The customer buys with a discount code or gift card that one of our emails or texts carried. That message gets the credit.",
      ),
      editableWindow: false,
    },
  };
}

export function attributionWindowUnitLabel(
  unit: (typeof ATTRIBUTION_TIME_UNITS)[number],
): string {
  switch (unit) {
    case TimeUnit.MINUTE:
      return t("Minutes");
    case TimeUnit.HOUR:
      return t("Hours");
    case TimeUnit.DAY:
      return t("Days");
  }
}
